import { Editor, Node, Path, Range, Text } from 'slate';

import { Queries } from './queries-object';
import { maxValueOf } from '../../util';
import {
  bookmarkEndType,
  bookmarkStartType,
  isMarkerElement,
  suggestionMarkerEndType,
  suggestionMarkerStartType,
} from '../types';
import type { AlineMarkerElement } from '../types';

export interface MarkerInfo {
  collapsed: boolean | undefined;
  nodes: AlineMarkerElement[];
  paired: boolean;
}

const markerQueries = {
  /**
   * Get a string uniquely identifying a pair of markers.
   */
  getMarkerKey: (marker: AlineMarkerElement) =>
    `${Queries.getMarkerType(marker)}${marker.markerId}`,

  getMarkerType: (marker: AlineMarkerElement) =>
    ({
      [bookmarkEndType]: 'bookmark',
      [bookmarkStartType]: 'bookmark',
      [suggestionMarkerEndType]: 'suggestion',
      [suggestionMarkerStartType]: 'suggestion',
    })[marker.type],

  /**
   * Get all markers in the range, including data on whether each of them is paired and if so,
   * whether it is collapsed. The range may be extended backward to include any markers immediately
   * before its start.
   */
  getMarkersInRange: (
    editor: Editor,
    range: Range
  ): {
    adjustedRange: Range;
    markerInfo: MarkerInfo[];
    markerInfoMap: Map<string, MarkerInfo>;
  } => {
    const markerInfoMap = new Map<string, MarkerInfo>();

    function processMarker(marker: AlineMarkerElement) {
      const key = Queries.getMarkerKey(marker);
      const markerInfo = markerInfoMap.get(key);

      if (markerInfo) {
        markerInfo.nodes.unshift(marker);
        markerInfo.paired = true;
        // If the marker isn't already known to be expanded, we now know it to be collapsed
        markerInfo.collapsed ??= true;
      } else {
        markerInfoMap.set(key, { collapsed: undefined, nodes: [marker], paired: false });
      }
    }

    /**
     * For each marker or pair of markers in the range, track whether it is paired and/or collapsed.
     * A collapsed marker pair is one with no non-empty nodes between each marker. Consume nodes in
     * reverse order so we can continue the algorithm backward afterwards.
     */
    for (const [node] of Editor.nodes(editor, { at: range, mode: 'lowest', reverse: true })) {
      if (isMarkerElement(node)) {
        processMarker(node);
      } else if (!Queries.nodeIsEmpty(node)) {
        // Encountered a non-empty node, so all currently unpaired markers must be expanded
        markerInfoMap.forEach(markerInfo => {
          if (!markerInfo.paired) {
            markerInfo.collapsed = false;
          }
        });
      }
    }

    const [initialStart, initialEnd] = Range.edges(range);
    let adjustedRange = range;

    /**
     * If there were markers within the range and the range starts at the start of a text node,
     * extend the start of the range backward to include any markers immediately prior to it.
     */
    if (markerInfoMap.size && initialStart.offset === 0) {
      // Start at the start of the range and iterate over its previous siblings
      let currentPath = initialStart.path;

      // Whether the most recent node was a marker
      let lastNodeWasMarker = false;

      // The path of the most recent text node that was immediately before a marker
      let lastTextBeforeMarker: Path | null = null;

      while (Path.hasPrevious(currentPath)) {
        currentPath = Path.previous(currentPath);
        const currentNode = Node.get(editor, currentPath);

        if (isMarkerElement(currentNode)) {
          processMarker(currentNode);
          lastNodeWasMarker = true;
          continue;
        }

        if (lastNodeWasMarker && Text.isText(currentNode)) {
          lastTextBeforeMarker = currentPath;
          lastNodeWasMarker = false;
        }

        if (!Queries.nodeIsEmpty(currentNode)) {
          break;
        }
      }

      /**
       * If we encountered any additional markers, extend the range to the end of the last text node
       * before the first such marker.
       */
      if (lastTextBeforeMarker) {
        const newStart = Editor.end(editor, lastTextBeforeMarker);
        adjustedRange = { anchor: newStart, focus: initialEnd };
      }
    }

    /**
     * Now that the paired property is final, we can unset collapsed for all markers that are still
     * unpaired, since it is meaningless for these markers.
     */
    markerInfoMap.forEach(markerInfo => {
      if (!markerInfo.paired) {
        markerInfo.collapsed = undefined;
      }
    });

    return {
      adjustedRange,
      markerInfo: Array.from(markerInfoMap.values()),
      markerInfoMap,
    };
  },

  /**
   * Returns the next available marker ID for the given marker type (e.g. bookmarkStart).
   * Uses the maximum markerId present in the document plus one.
   */
  getNextMarkerId: (editor: Editor, type: AlineMarkerElement['type']): number => {
    const markers = Array.from(
      Editor.nodes(editor, {
        at: [],
        match: (node): node is AlineMarkerElement => isMarkerElement(node) && node.type === type,
      }),
      ([node]) => node
    );
    return maxValueOf(markers, 'markerId', 0) + 1;
  },
};

export default markerQueries;
